import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, LayoutDashboard, Users, BookOpen, FileBadge, Gamepad2,
    Gift, MessageSquareText, Palette, BarChart3, ArrowRight, Command,
} from 'lucide-react';
import { api } from '@/lib/api';

const STATIC_COMMANDS = [
    { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, kw: 'home overview kpi' },
    { label: 'Users & Roles', to: '/admin/users', icon: Users, kw: 'people team employees' },
    { label: 'Courses', to: '/admin/videos', icon: BookOpen, kw: 'training content videos' },
    { label: 'AI Analytics', to: '/admin/ai-analytics', icon: MessageSquareText, kw: 'ai chat assistant' },
    { label: 'Certificates', to: '/admin/certificates', icon: FileBadge, kw: 'certified verify' },
    { label: 'Learning Arcade', to: '/admin/games', icon: Gamepad2, kw: 'games arcade ludo' },
    { label: 'Rewards & Badges', to: '/admin/rewards', icon: Gift, kw: 'incentives points' },
    { label: 'Reports', to: '/admin/reports', icon: BarChart3, kw: 'analytics insights' },
    { label: 'Brand Settings', to: '/admin/brand', icon: Palette, kw: 'logo colors theme' },
];

export default function CommandPalette({ open, onClose }) {
    const [q, setQ] = useState('');
    const [users, setUsers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [idx, setIdx] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) { setQ(''); setIdx(0); return; }
        api.get('/admin/users').then((r) => setUsers(r.data || [])).catch(() => {});
        api.get('/courses?published_only=false').then((r) => setCourses(r.data || [])).catch(() => {});
    }, [open]);

    useEffect(() => {
        const handler = (e) => {
            if (!open) return;
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => i + 1); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
            else if (e.key === 'Enter') {
                e.preventDefault();
                const item = results[idx];
                if (item) go(item);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    });

    if (!open) return null;

    const lc = q.toLowerCase();
    const navHits = STATIC_COMMANDS.filter((c) => !q || c.label.toLowerCase().includes(lc) || c.kw.includes(lc)).map((c) => ({ ...c, group: 'Navigate' }));
    const userHits = q ? users.filter((u) => (u.full_name || '').toLowerCase().includes(lc) || (u.email || '').toLowerCase().includes(lc)).slice(0, 5).map((u) => ({
        group: 'Learners', label: u.full_name, sub: `${u.email} · ${u.department || '—'}`,
        icon: Users, to: `/admin/users?q=${encodeURIComponent(u.email)}`,
    })) : [];
    const courseHits = q ? courses.filter((c) => (c.title || '').toLowerCase().includes(lc)).slice(0, 5).map((c) => ({
        group: 'Courses', label: c.title, sub: `${c.category} · ${c.modules?.length || 0} modules`,
        icon: BookOpen, to: `/learn/courses/${c.id}`,
    })) : [];

    const results = [...navHits, ...userHits, ...courseHits];
    const safeIdx = Math.min(idx, Math.max(0, results.length - 1));

    const go = (item) => { navigate(item.to); onClose(); };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4" onClick={onClose} data-testid="cmd-palette">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input
                        autoFocus value={q} onChange={(e) => { setQ(e.target.value); setIdx(0); }}
                        placeholder="Search learners, courses, or jump to a page…"
                        className="flex-1 bg-transparent outline-none text-sm py-1"
                        data-testid="cmd-input"
                    />
                    <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">ESC</kbd>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-2">
                    {!results.length && <div className="text-center text-sm text-slate-500 py-8">No matches.</div>}
                    {Object.entries(results.reduce((acc, r, i) => {
                        (acc[r.group] = acc[r.group] || []).push({ ...r, _i: i });
                        return acc;
                    }, {})).map(([group, items]) => (
                        <div key={group} className="mb-1">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-3 py-1">{group}</div>
                            {items.map((item) => (
                                <button
                                    key={item._i}
                                    onMouseEnter={() => setIdx(item._i)}
                                    onClick={() => go(item)}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm ${item._i === safeIdx ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                                    data-testid={`cmd-result-${item._i}`}
                                >
                                    <item.icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-slate-900 truncate">{item.label}</div>
                                        {item.sub && <div className="text-xs text-slate-500 truncate">{item.sub}</div>}
                                    </div>
                                    {item._i === safeIdx && <ArrowRight className="h-3.5 w-3.5 text-slate-400" />}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
                <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-3 text-[10px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">↑↓</kbd> navigate</span>
                    <span className="inline-flex items-center gap-1"><kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono">↵</kbd> open</span>
                    <span className="ml-auto inline-flex items-center gap-1"><Command className="h-3 w-3" />K to open anywhere</span>
                </div>
            </div>
        </div>
    );
}
